import { useState } from "react";
import { FileUp, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { getErrorMessage } from "@/hooks/use-error-toast";
import { useCreateInvoiceMutation } from "@/services/invoices/invoicesApi";
import { INVOICE_ACCEPT, validateInvoicePdf } from "@/lib/uploadValidation";
import { cn } from "@/lib/utils";

export function InvoiceUploadOffcanvas({ open, onOpenChange, onUploaded }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [createInvoice, { isLoading: isUploading }] = useCreateInvoiceMutation();

  const [wasOpen, setWasOpen] = useState(open);
  // Bumping this remounts the file input, which is the only way to clear a
  // file picker's value without reaching into the DOM.
  const [inputGeneration, setInputGeneration] = useState(0);

  // Clear the previous attempt as the panel opens, so a failed upload's error
  // is not still on screen for the next one.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSelectedFile(null);
      setError("");
      setStatus("");
      setInputGeneration((value) => value + 1);
    }
  }

  const resetFileInput = () => {
    setSelectedFile(null);
    setInputGeneration((value) => value + 1);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setError("");
    setStatus("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setStatus("");

    // `accept` only filters the picker dialog; it is advisory, and an "All
    // files" override walks straight past it.
    const check = validateInvoicePdf(selectedFile);
    if (!check.ok) {
      setError(check.reason);
      if (selectedFile) resetFileInput();
      return;
    }

    const formData = new FormData();
    formData.append("File", selectedFile);

    try {
      await createInvoice(formData).unwrap();
      setStatus("Invoice uploaded.");
      toast.success("Invoice uploaded");
      onUploaded?.();
      window.setTimeout(() => onOpenChange(false), 500);
    } catch (uploadError) {
      setError(getErrorMessage(uploadError, "Upload failed."));
      resetFileInput();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        overlayClassName="filter-offcanvas-veil"
        className="filter-offcanvas"
      >
        <div className="filter-offcanvas-panel">
          <form className="flex h-full min-h-0 flex-col" onSubmit={handleSubmit}>
            <SheetHeader className="filter-offcanvas-header">
              <div className="min-w-0 flex-1">
                <SheetTitle className="text-base font-semibold tracking-tight">
                  Choose Invoice PDF file
                </SheetTitle>
                <SheetDescription>
                  Upload a supplier invoice PDF to start extraction and review.
                </SheetDescription>
              </div>
              <SheetClose
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="filter-offcanvas-close shadow-none"
                  />
                }
              >
                <X className="size-4" />
                <span className="sr-only">Close</span>
              </SheetClose>
            </SheetHeader>

            <div className="filter-offcanvas-body">
              <section className="filter-offcanvas-section">
                <div className="filter-offcanvas-section-head">
                  <h3 className="filter-offcanvas-section-title">Invoice file</h3>
                  <p className="filter-offcanvas-section-hint">Only .pdf files are accepted.</p>
                </div>

                <label
                  htmlFor="invoice-pdf-file"
                  className={cn(
                    "invoice-upload-dropzone",
                    selectedFile && "invoice-upload-dropzone-has-file",
                  )}
                >
                  <FileUp className="size-5 shrink-0 text-primary" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">
                      {selectedFile ? selectedFile.name : "Select a PDF file"}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {selectedFile
                        ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                        : "Click to browse your files"}
                    </span>
                  </span>
                  <input
                    key={inputGeneration}
                    id="invoice-pdf-file"
                    name="file"
                    type="file"
                    accept={INVOICE_ACCEPT}
                    required
                    disabled={isUploading}
                    onChange={handleFileChange}
                    className="sr-only"
                  />
                </label>

                {error && (
                  <p className="mt-3 text-sm text-destructive" role="alert">
                    {error}
                  </p>
                )}
                {status && (
                  <output className="mt-3 block text-sm text-(--semantic-success-fg)">
                    {status}
                  </output>
                )}
                {isUploading && (
                  <output className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    Uploading…
                  </output>
                )}
              </section>
            </div>

            <SheetFooter className="filter-offcanvas-footer">
              <Button
                type="button"
                variant="outline"
                className="h-9 flex-1 rounded-[0.8rem] border-line shadow-none"
                disabled={isUploading}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                className="h-9 flex-1 rounded-[0.8rem]"
                disabled={isUploading}
              >
                Upload
              </Button>
            </SheetFooter>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
